/**
 * Auth hooks (TanStack Query)
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import i18n from '@/i18n';
import { authApi } from '../api/endpoints/auth';
import { ApiError, isTransportError, isServerFaultError } from '../api/client';
import { useAuthStore } from '../stores/auth';
import { errorToast } from '@/hooks/errorToast';
import { userFromAuthResponse } from '../api/types';
import { reconcileIdentity } from '@/lib/authIdentity';
import { refreshAdminTier } from '@/lib/adminTier';
import { fetchLaunchableAgents } from '@/hooks/useAgentCatalogue';
import { getSafeReturnTo } from '@/lib/safeReturnTo';
import type { LoginRequest, SetPasswordRequest } from '../api/types';

/** Error code the login endpoint returns (403) when an unverified password
 *  account tries to sign in. The Login page renders a specific, actionable
 *  notice for it, so the generic "login failed" toast is suppressed. */
export const EMAIL_NOT_VERIFIED_CODE = 'EMAIL_NOT_VERIFIED';

/** True when an error is the email-not-verified gate (403 EMAIL_NOT_VERIFIED). */
export function isEmailNotVerifiedError(error: unknown): boolean {
  return error instanceof ApiError && error.code === EMAIL_NOT_VERIFIED_CODE;
}

/**
 * The i18n key describing an auth failure. `rejectedKey` is the copy for a
 * genuine rejection by the server ("wrong credentials", "registration failed")
 * and is used ONLY when the server actually rejected. A request it never
 * answered — no connection, a timeout, or our own 5xx — says nothing about
 * what the user typed, and blaming their input for it hides the layer that
 * really broke.
 *
 * Returns the KEY, not the translated string, so the inline banners can
 * translate it with their own `t` from useTranslation (which re-renders on a
 * language switch) while the toasts here use the `i18n.t` they already hold.
 * One classifier, two rendering contexts — the branching itself must not be
 * re-derived per call site, which is the mistake this whole change is about.
 */
export function authFailureKey(error: unknown, rejectedKey: string): string {
  if (isTransportError(error)) return 'errors.connection';
  if (isServerFaultError(error)) return 'errors.serverFault';
  // Register's own 403 is the closed preview: the address is not on the
  // allowlist (rule 1), which is a state of the product, not a mistake in
  // what the person typed. Login's 403s mean other things (a period that
  // refuses, an unverified provider email), so the branch is taken only in
  // the register context the rejectedKey names.
  if (
    rejectedKey === 'auth.registerFailed' &&
    error instanceof ApiError &&
    error.status === 403
  ) {
    return 'auth.registerClosed';
  }
  return rejectedKey;
}

/**
 * The register 403's branch above stays: /register is no longer a form (the
 * invitation link lands on /set-password), but the API door remains for an
 * allowlisted address, and its refusal is still the closed preview — the
 * panel at /register and any direct API caller read the same key.
 */
export function useLogin() {
  const { setAuth } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: LoginRequest) => authApi.login(data),
    onSuccess: (response) => {
      setAuth(response.access_token, userFromAuthResponse(response));
      // HandleLogin always hardcodes admin_view=false and never populates the
      // nested user.is_platform_admin (only /me computes that OpenFGA Check) —
      // so a returning admin's login snapshot is unconditionally wrong until
      // reconciled. Fire-and-forget, mirroring useAuthInit's fast path: the
      // token is freshly minted (definitely valid), so the UI renders now and
      // corrects itself in the background rather than blocking on the extra
      // round trip.
      void reconcileIdentity(response.access_token, {
        onlyIfTokenStillActive: true,
      });
      // The tier and the launchable list are lazy post-boot reads
      // (care-app/ADR-0015): neither blocks the session.
      void refreshAdminTier();
      void fetchLaunchableAgents();
      // Invalidate any cached queries on login
      void queryClient.invalidateQueries();
    },
    onError: (error) => {
      // The unverified-account gate gets a dedicated inline notice on the Login
      // page (with a resend action) — a generic destructive toast would be both
      // redundant and misleading ("login failed" when the fix is "verify").
      if (isEmailNotVerifiedError(error)) return;
      errorToast({
        context: 'login',
        description: i18n.t(authFailureKey(error, 'auth.loginFailed')),
      });
    },
  });
}

/**
 * Consume an invitation link by setting the password it proves. On success the
 * backend has stamped `activated` and minted the session — persist it through
 * the same path as login (the /home guard routes a not-yet-onboarded account
 * on to /onboarding). The refusals (dead link, already set, period not begun)
 * are the SetPassword page's states; no toast here — the form owns the copy.
 */
export function useSetPassword() {
  const { setAuth } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SetPasswordRequest) => authApi.setPassword(data),
    onSuccess: (response) => {
      setAuth(response.access_token, userFromAuthResponse(response));
      void reconcileIdentity(response.access_token, {
        onlyIfTokenStillActive: true,
      });
      void refreshAdminTier();
      void fetchLaunchableAgents();
      void queryClient.invalidateQueries();
    },
  });
}

/**
 * Verify an email via the raw token from the link. Resolves to a
 * VerifyEmailResult (the shape the verify link answers): on a
 * FRESH consumption the server issues a session (same refresh/access cookies
 * as login), so we persist it through the SAME auth-store path as useLogin —
 * the user lands logged in. On a REPLAY of an already-consumed token
 * (`verified: false`) there is NO session to persist — see
 * AlreadyVerifiedResponse for why re-minting one would be unsafe — so this is
 * a deliberate no-op; the caller (VerifyEmail page) routes that case to login
 * instead of /home. A bad/expired/unknown token surfaces as a 410 ApiError;
 * the caller renders the invalid-link state. No toast here: the page owns
 * the error UI.
 */
export function useVerifyEmail() {
  const { setAuth } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (token: string) => authApi.verifyEmail({ token }),
    onSuccess: (result) => {
      if (!result.verified) return;
      setAuth(result.auth.access_token, userFromAuthResponse(result.auth));
      void reconcileIdentity(result.auth.access_token, {
        onlyIfTokenStillActive: true,
      });
      void refreshAdminTier();
      void fetchLaunchableAgents();
      void queryClient.invalidateQueries();
    },
  });
}

/** Resend a verification email. Generic by design (no account enumeration); the
 *  UI just confirms the request was accepted. No auth-store side effects. */
export function useResendVerification() {
  return useMutation({
    mutationFn: (email: string) => authApi.resendVerification({ email }),
  });
}

export function useLogout() {
  const { logout } = useAuthStore();

  // logout() tears down all user-scoped state itself — including clearing the
  // TanStack Query cache (see auth store's tearDownUserScopedState) — so this hook
  // no longer clears the cache separately; that keeps one teardown chokepoint
  // shared with the silent-expiry / identity-swap paths.
  return useMutation({
    mutationFn: () => authApi.logout(),
    onSuccess: () => {
      logout();
    },
    onError: () => {
      // Even if server logout fails, clear local state
      logout();
    },
  });
}

/**
 * Hook to start OAuth flow. Fetches authorization URL from backend, then redirects.
 * Saves CSRF state in sessionStorage (Firebase Hosting strips all cookies except __session).
 */
export function useOAuthLogin() {
  return useMutation({
    mutationFn: async (provider: string) => {
      const response = await authApi.oauthAuthorize(provider);
      sessionStorage.setItem('oauth_state', response.state);
      // The provider round-trip discards SPA context, so a returnTo on the
      // login URL (the MCP consent route /oauth/authorize, sub-app hops) must
      // be stashed and resumed by OAuthCallback — the SSO twin of
      // Login.handleSubmit's returnTo handling. Validated at stash time;
      // always overwritten so a stale value can't divert a later login.
      const returnTo = getSafeReturnTo(new URLSearchParams(window.location.search));
      if (returnTo) {
        sessionStorage.setItem('oauth_return_to', returnTo);
      } else {
        sessionStorage.removeItem('oauth_return_to');
      }
      window.location.href = response.url;
    },
    onError: (error) => {
      errorToast({
        context: 'oauthLogin',
        description: i18n.t(authFailureKey(error, 'auth.loginFailed')),
      });
    },
  });
}

/**
 * Hook to complete OAuth flow (exchange code for session).
 */
export function useOAuthCallback() {
  const { setAuth } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ provider, code, state }: { provider: string; code: string; state: string }) =>
      authApi.oauthCallback(provider, code, state),
    onSuccess: (response) => {
      setAuth(response.access_token, userFromAuthResponse(response));
      // Same gap as useLogin — and the highest-stakes instance of it: the
      // founder's very first OAuth login seeds platform-admin server-side
      // (asyncMaybeSeedInitialAdmin) but this response's nested user still
      // never carries is_platform_admin, so without this the admin nav
      // wouldn't appear until a reload even on the seeding login itself.
      void reconcileIdentity(response.access_token, {
        onlyIfTokenStillActive: true,
      });
      void queryClient.invalidateQueries();
      // The admin tier and the launchable list are NOT fired here — the
      // callback page fires them in its awaited mutateAsync() continuation,
      // after setAuth above has committed the session. (Placement note for
      // anyone reasoning about callback dropping: in the installed
      // @tanstack/query-core 5.x, hook-level onSuccess runs at mutation
      // level, unconditionally, before mutateAsync resolves — only the
      // per-mutate() second-argument callbacks are observer-gated. The
      // continuation is chosen for clarity of ordering, not because this
      // onSuccess is droppable.)
    },
    onError: (error) => {
      errorToast({
        context: 'oauthCallback',
        description: i18n.t(authFailureKey(error, 'auth.loginFailed')),
      });
    },
  });
}

/**
 * Hook to get current user
 */
export function useCurrentUser() {
  return useAuthStore((state) => state.user);
}
